import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Text, toast } from "@performyard/py-components";
import { Spinner } from "@performyard/py-components";

import { CalendarCard } from "./components/CalendarCard";
import { CalendarDisconnectionModal } from "./components/CalendarDisconnectionModal";
import { CalendarSelectionModal } from "./components/CalendarSelectionModal";
import { GoogleCalendarIcon } from "./components/GoogleCalendarIcon";
import { OutlookCalendarIcon } from "./components/OutlookCalendarIcon";

import ErrorPage from "../../../../pages/Error/ErrorPage";

import {
	useCalendarIntegrationOAuthUrl,
	useCompanyCalendarIntegrationSettings,
	useDisconnectCalendarIntegration,
	useUserCalendarIntegrationStatus,
	useUserCalendars,
} from "../../../../api/hooks/defs/calendar-integration";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { currentUserQuery, userCalendarIntegrationStatusQuery } from "../../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../../api/queries/dataLoader";

import { CALENDAR_PROVIDERS, CalendarProvider } from "./config";

// Data loader function
export const calendarViewLoader = () => {
	const queries: (() => SimplifiedQuery)[] = [userCalendarIntegrationStatusQuery];

	return dataLoader([[currentUserQuery], queries]);
};

export const CalendarView = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	// Check feature flag and company calendar integration setting to ensure calendar integration is enabled
	const { value: ffIsCalendarIntegrationEnabled } = useFeatureFlagQuery("ff-is-calendar-integration-enabled", false);
	const { data: companyCalendarIntegrationSettings, isLoading: isCompanyCalendarIntegrationSettingsLoading } =
		useCompanyCalendarIntegrationSettings({
			enabled: ffIsCalendarIntegrationEnabled,
		});
	const isCalendarIntegrationVisible = companyCalendarIntegrationSettings?.enabled && ffIsCalendarIntegrationEnabled;

	const [showCalendarModal, setShowCalendarModal] = useState(false);
	const [showDisconnectConfirmation, setShowDisconnectConfirmation] = useState(false);

	// Google calendar auth initiation
	const { initiateCalendarIntegrationAuth: initiateGoogleAuth, isLoading: isGoogleAuthLoading } =
		useCalendarIntegrationOAuthUrl({
			provider: CALENDAR_PROVIDERS.GOOGLE as CalendarProvider,
		});

	// Outlook calendar auth initiation
	const { initiateCalendarIntegrationAuth: initiateOutlookAuth, isLoading: isOutlookAuthLoading } =
		useCalendarIntegrationOAuthUrl({
			provider: CALENDAR_PROVIDERS.OUTLOOK as CalendarProvider,
		});

	// User calendar status
	const {
		data: userCalendarStatus,
		isLoading: isStatusLoading,
		refetch: refetchStatus,
	} = useUserCalendarIntegrationStatus({
		enabled: ffIsCalendarIntegrationEnabled && companyCalendarIntegrationSettings?.enabled === true,
	});

	// Disconnect calendar integration
	const { mutateAsync: disconnectCalendarIntegration, isPending: isDisconnecting } =
		useDisconnectCalendarIntegration();

	// Fetch calendars when user is connected
	const isConnected = userCalendarStatus?.isConnected;
	const provider = userCalendarStatus?.provider;
	const { data: calendarsData, isLoading: isCalendarsLoading } = useUserCalendars({
		provider: provider as "google" | "outlook",
		enabled: isConnected && !!provider,
	});

	// Handle OAuth callback messages from URL parameters
	useEffect(() => {
		const successParam = searchParams.get("success");

		if (successParam === "true") {
			toast.success("Calendar successfully connected!");
			// Refetch user calendar status after successful connection
			refetchStatus();
			// Clean up URL by removing the success parameter
			searchParams.delete("success");
			setSearchParams(searchParams, { replace: true });
		} else if (successParam === "false") {
			toast.error("Failed to connect calendar. Please try again.");
			// Clean up URL by removing the success parameter
			searchParams.delete("success");
			setSearchParams(searchParams, { replace: true });
		}
	}, [searchParams, setSearchParams, refetchStatus]);

	const handleConnect = (provider: CalendarProvider) => {
		if (provider === CALENDAR_PROVIDERS.GOOGLE) {
			initiateGoogleAuth();
		} else if (provider === CALENDAR_PROVIDERS.OUTLOOK) {
			initiateOutlookAuth();
		}
	};

	const handleDisconnect = async () => {
		setShowDisconnectConfirmation(true);
	};

	const handleConfirmDisconnect = async () => {
		await disconnectCalendarIntegration();
		setShowDisconnectConfirmation(false);
	};

	// Find the selected calendar name and determine if we should show manage button
	const selectedCalendar = calendarsData?.calendars?.find((cal) => cal.id === userCalendarStatus?.selectedCalendarId);
	const connectedCalendarName = selectedCalendar?.name;
	const isConnectedCalendarPrimary = selectedCalendar?.primary || false;
	const shouldShowManageButton = (calendarsData?.calendars?.length || 0) > 1;

	const handleCloseModal = () => {
		setShowCalendarModal(false);
		// Refetch status to check if calendar was selected
		refetchStatus();
	};

	if (isCompanyCalendarIntegrationSettingsLoading || isCalendarsLoading) {
		return (
			<div className="flex h-[208px] w-full">
				<div className="flex h-full w-full items-center justify-center">
					<Spinner size={32} />
				</div>
			</div>
		);
	}

	// If feature flag or company calendar integration setting is disabled, show access denied page
	if (!isCalendarIntegrationVisible) {
		return <ErrorPage code={403} />;
	}

	return (
		<div className="flex w-full flex-col gap-6">
			{isStatusLoading ? (
				<div className="flex h-full w-full items-center justify-center">
					<Spinner size={32} />
				</div>
			) : (
				<>
					<div className="flex w-[656px] flex-col gap-4">
						<Text size="heading-5" weight="semi-bold">
							Calendar
						</Text>
						<Text size="paragraph-2" weight="regular">
							You can link a calendar for use in the PerformYard platform. However, you can only connect
							one calendar at a time.
						</Text>
					</div>
					<div className="flex flex-col gap-4">
						<CalendarCard
							title="Google Calendar"
							icon={<GoogleCalendarIcon />}
							isConnected={!!(isConnected && provider === "google")}
							isDisabled={
								isGoogleAuthLoading ||
								isStatusLoading ||
								isDisconnecting ||
								// Disable if another provider is connected
								(userCalendarStatus?.isConnected && provider !== "google")
							}
							onConnect={() => handleConnect(CALENDAR_PROVIDERS.GOOGLE as CalendarProvider)}
							onDisconnect={handleDisconnect}
							onManage={() => setShowCalendarModal(true)}
							connectedCalendarName={provider === "google" ? connectedCalendarName : undefined}
							showManageButton={provider === "google" ? shouldShowManageButton : false}
							isConnectedCalendarPrimary={provider === "google" ? isConnectedCalendarPrimary : false}
						/>
						<CalendarCard
							title="Outlook Calendar"
							icon={<OutlookCalendarIcon />}
							isConnected={!!(isConnected && provider === "outlook")}
							isDisabled={
								isOutlookAuthLoading ||
								isStatusLoading ||
								isDisconnecting ||
								// Disable if another provider is connected
								(userCalendarStatus?.isConnected && provider !== "outlook")
							}
							onConnect={() => handleConnect(CALENDAR_PROVIDERS.OUTLOOK as CalendarProvider)}
							onDisconnect={handleDisconnect}
							onManage={() => setShowCalendarModal(true)}
							connectedCalendarName={provider === "outlook" ? connectedCalendarName : undefined}
							showManageButton={provider === "outlook" ? shouldShowManageButton : false}
							isConnectedCalendarPrimary={provider === "outlook" ? isConnectedCalendarPrimary : false}
						/>
					</div>
					<CalendarSelectionModal isOpen={showCalendarModal} onClose={handleCloseModal} />
					<CalendarDisconnectionModal
						isOpen={showDisconnectConfirmation}
						isDisconnecting={isDisconnecting}
						handleConfirmDisconnect={handleConfirmDisconnect}
						handleCancelDisconnect={() => setShowDisconnectConfirmation(false)}
					/>
				</>
			)}
		</div>
	);
};
